import * as Sentry from '@sentry/react';
import { ScriptixDescendant } from '@utils/types/editors/transcripts/transcript.ts';
import { Editor, Transforms } from 'slate';
import { VoiceTimelineSpan } from '@utils/types/organization/voice-profile.ts';
import { rowBelongsToLabel } from '@utils/editors/voice-sample-ranges.ts';

export const replaceSpeakersInEditorChildren = (
  editor: Editor,
  oldSpeaker: string,
  newSpeaker: string
) => {
  try {
    // Use Slate's Transforms API to properly update nodes
    const pathsToUpdate: number[][] = [];

    for (let i = 0; i < editor.children.length; i++) {
      const child = editor.children[i] as ScriptixDescendant;
      if (child.speaker === oldSpeaker) {
        pathsToUpdate.push([i]);
      }
    }

    // Apply transformations using Slate's API
    Editor.withoutNormalizing(editor, () => {
      pathsToUpdate.forEach(path => {
        Transforms.setNodes(
          editor,
          { speaker: newSpeaker } as Partial<ScriptixDescendant>,
          { at: path }
        );
      });
    });
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'editor',
      message: 'replaceSpeakersInEditorChildren failed',
      level: 'error',
      data: { oldSpeaker, newSpeaker, error: String(error) }
    });
  }
};

/** Name the rows of ONE detected voice, found by position in the source audio.
 *
 * The display-name version above answers "rename everyone currently called X",
 * which is the right behaviour for a manual rename but the wrong one for a
 * voice identity: two detected voices routinely share a display name — before
 * anyone is named, and again the moment the first person is named — so a
 * name-matched sweep renames the other person's rows too, and the second
 * assignment wipes out the first.
 *
 * Positions come from the backend's own diarization timeline, which is also
 * what the identity decision is keyed on, so the rows renamed here are exactly
 * the rows the decision covers. */
export const renameSpeakerByTimelineInEditorChildren = (
  editor: Editor,
  timeline: VoiceTimelineSpan[],
  speakerLabel: string,
  newSpeaker: string
) => {
  try {
    const pathsToUpdate: number[][] = [];

    for (let i = 0; i < editor.children.length; i++) {
      const child = editor.children[i] as ScriptixDescendant;
      if (child.speaker === newSpeaker) continue;
      if (rowBelongsToLabel(timeline, speakerLabel, child.start, child.stop)) {
        pathsToUpdate.push([i]);
      }
    }

    Editor.withoutNormalizing(editor, () => {
      pathsToUpdate.forEach(path => {
        Transforms.setNodes(
          editor,
          { speaker: newSpeaker } as Partial<ScriptixDescendant>,
          { at: path }
        );
      });
    });
  } catch (error) {
    Sentry.addBreadcrumb({
      category: 'editor',
      message: 'renameSpeakerByTimelineInEditorChildren failed',
      level: 'error',
      data: { speakerLabel, newSpeaker, error: String(error) }
    });
  }
};
